import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import "./HireModal.css";
import { HireModalContext } from "./HireModalContext";
import { EMPLOYEE_AVATARS, EMPLOYEE_HUE } from "@/components/VirtualEmployeesSection/employees";
import { dictionaries } from "@/i18n/dictionary";
import { useLocale } from "@/i18n/useLocale";
import { sendContactForm } from "@/services/contactService";
import { getRecaptchaToken } from "@/services/recaptcha";

const WHATSAPP_URL_BASE = "https://wa.me/5493413070683";

interface HireFormProps {
  employeeId: string;
  /** Desde donde se abrio el modal; si falta, se asume la home. */
  source?: string;
  onClose: () => void;
}

/**
 * Formulario de dos datos -nombre y celular- para contratar un empleado
 * virtual. Va al mismo endpoint que el resto de los formularios del sitio
 * (api/public/contact.php: reCAPTCHA, LeadStore para el panel y mail de
 * aviso). El `service` lleva el puesto, asi tanto el mail como el panel dicen
 * por cual empleado virtual se contactaron.
 *
 * Se remonta en cada apertura (key en el proveedor): arranca limpio sin
 * resetear campo por campo.
 */
function HireForm({ employeeId, source, onClose }: HireFormProps) {
  const { t, locale } = useLocale();
  const hm = t.hireModal;
  const nameId = useId();
  const phoneId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [fieldError, setFieldError] = useState<{ name?: string; phone?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");

  const role = t.virtualTeam.members.find((m) => m.id === employeeId)?.role ?? "";
  // El aviso lo lee el equipo de WAIA: siempre con el nombre del puesto en
  // español, aunque la visita haya llegado por el sitio en ingles.
  const roleEs = dictionaries.es.virtualTeam.members.find((m) => m.id === employeeId)?.role ?? role;
  const Avatar = EMPLOYEE_AVATARS[employeeId];
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    const cleanName = name.trim();
    const digits = phone.replace(/\D/g, "");
    const errors: { name?: string; phone?: string } = {};

    if (cleanName.length < 2) errors.name = hm.errName;
    if (digits.length < 8 || digits.length > 15) errors.phone = hm.errPhone;

    setFieldError(errors);
    if (errors.name) {
      nameRef.current?.focus();
      return;
    }
    if (errors.phone) {
      phoneRef.current?.focus();
      return;
    }

    setState("sending");

    if (!recaptchaSiteKey) {
      setState("idle");
      setFormError(t.wizard.errRecaptchaKey);
      return;
    }

    let token: string;
    try {
      token = await getRecaptchaToken(recaptchaSiteKey, {
        load: t.wizard.errRecaptchaLoad,
        unavailable: t.wizard.errRecaptchaUnavailable,
      });
    } catch {
      setState("idle");
      setFormError(t.wizard.errRecaptchaVerify);
      return;
    }

    if (!token) {
      setState("idle");
      setFormError(t.wizard.errRecaptchaVerify);
      return;
    }

    const result = await sendContactForm({
      name: cleanName,
      // No se pide: el backend lo acepta vacio (ver Validator.php) y el
      // contacto es por celular.
      email: "",
      phone: phone.trim(),
      country: "Argentina",
      company: "",
      service: `Empleado virtual · ${roleEs}`,
      message: `Quiere contratar el empleado virtual: ${roleEs}. Dejó nombre y celular desde el modal rápido ${
        source ? `de la nota "${source}"` : "de la home"
      }${locale === "en" ? " (sitio en inglés)" : ""}.`,
      recaptcha_token: token,
    });

    if (result.success) {
      setState("done");
    } else {
      setState("idle");
      setFormError(result.message);
    }
  }

  const whatsappUrl = `${WHATSAPP_URL_BASE}?text=${encodeURIComponent(
    hm.waMessage.replace("{role}", role),
  )}`;

  return (
    <div className="hire-modal__panel">
      <div className="hire-modal__avatar" aria-hidden="true">
        {Avatar && <Avatar />}
      </div>

      {state === "done" ? (
        <div className="hire-modal__done" role="status">
          <h2 id="hire-modal-title" className="hire-modal__title">
            {hm.successTitle.replace("{name}", name.trim().split(/\s+/)[0] ?? "")}
          </h2>
          <p className="hire-modal__subtitle">{hm.successMsg.replace("{role}", role)}</p>

          <a className="hire-modal__whatsapp" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            {hm.waLabel}
          </a>
          <button type="button" className="hire-modal__submit" onClick={onClose}>
            {hm.done}
          </button>
        </div>
      ) : (
        <form className="hire-modal__form" onSubmit={handleSubmit} noValidate>
          <p className="hire-modal__eyebrow">
            {hm.eyebrow} · {role}
          </p>
          <h2 id="hire-modal-title" className="hire-modal__title">
            {hm.title}
          </h2>
          <p className="hire-modal__subtitle">{hm.subtitle.replace("{role}", role)}</p>

          <div className="hire-modal__field">
            <label htmlFor={nameId}>{hm.name}</label>
            <input
              id={nameId}
              ref={nameRef}
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={state === "sending"}
              aria-invalid={Boolean(fieldError.name)}
              aria-describedby={fieldError.name ? `${nameId}-err` : undefined}
            />
            {fieldError.name && (
              <p id={`${nameId}-err`} className="hire-modal__error" role="alert">
                {fieldError.name}
              </p>
            )}
          </div>

          <div className="hire-modal__field">
            <label htmlFor={phoneId}>{hm.phone}</label>
            <input
              id={phoneId}
              ref={phoneRef}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="341 555 1234"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={state === "sending"}
              aria-invalid={Boolean(fieldError.phone)}
              aria-describedby={`${phoneId}-hint${fieldError.phone ? ` ${phoneId}-err` : ""}`}
            />
            <p id={`${phoneId}-hint`} className="hire-modal__hint">
              {hm.phoneHint}
            </p>
            {fieldError.phone && (
              <p id={`${phoneId}-err`} className="hire-modal__error" role="alert">
                {fieldError.phone}
              </p>
            )}
          </div>

          {formError && (
            <p className="hire-modal__error" role="alert">
              {formError}
            </p>
          )}

          <button type="submit" className="hire-modal__submit" disabled={state === "sending"}>
            {state === "sending" ? hm.submitting : hm.submit}
          </button>

          <p className="hire-modal__fine">{hm.fine}</p>
        </form>
      )}
    </div>
  );
}

/**
 * Un unico modal de contratacion para toda la home: lo abren la seccion de
 * empleados y el slide del hero con useHireModal().open(idDelEmpleado).
 */
function HireModalProvider({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [request, setRequest] = useState<{ id: string; source?: string; count: number } | null>(null);

  // showModal despues del render -no en el handler- para que el modal abra ya
  // con el empleado elegido y no un cuadro con el contenido anterior.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (request && dialog && !dialog.open) dialog.showModal();
  }, [request]);

  const api = useMemo(
    () => ({
      open: (employeeId: string, source?: string) =>
        setRequest((prev) => ({ id: employeeId, source, count: (prev?.count ?? 0) + 1 })),
    }),
    [],
  );

  const close = () => dialogRef.current?.close();
  const hue = request ? EMPLOYEE_HUE[request.id] : undefined;

  return (
    <HireModalContext.Provider value={api}>
      {children}

      <dialog
        ref={dialogRef}
        className="hire-modal"
        style={{ "--hue": hue ?? 320 } as CSSProperties}
        aria-labelledby="hire-modal-title"
        onClick={(e) => {
          // El click en el fondo oscuro le llega al propio <dialog>.
          if (e.target === e.currentTarget) close();
        }}
      >
        {request && (
          <HireForm key={request.count} employeeId={request.id} source={request.source} onClose={close} />
        )}

        {/* Despues del formulario a proposito: asi el primer elemento
            enfocable al abrir es el campo de nombre, no el boton de cerrar. */}
        <button type="button" className="hire-modal__close" aria-label={t.hireModal.close} onClick={close}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </dialog>
    </HireModalContext.Provider>
  );
}

export default HireModalProvider;
